/**
 * IndustryEBITDAComparisonChart
 *
 * Clustered bar chart showing absolute EBITDA per company per year.
 * Replaces the median-only EBITDAComparisonChart for industry benchmarks.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCurrencyValue } from '../../financial/utils/formatters';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries } from '../../../types/industryResearch';

const COMPANY_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#84cc16',
  '#ec4899', '#14b8a6',
];

interface Props {
  companies: CompanyTimeSeries[];
  region?: RegionConfig;
}

export default function IndustryEBITDAComparisonChart({ companies, region }: Props) {
  // Build unified year list across all companies
  const yearSet = new Set<number>();
  companies.forEach((co) => co.data.forEach((r) => yearSet.add(r.year)));
  const years = Array.from(yearSet).sort((a, b) => a - b);

  const chartData = years.map((yr) => {
    const entry: Record<string, number | string | null> = {
      year: formatFiscalYear(yr, region),
    };
    companies.forEach((co) => {
      const rec = co.data.find((d) => d.year === yr);
      entry[co.ticker ?? co.company_name] = rec?.ebitda ?? null;
    });
    return entry;
  });

  const unit = region?.currency?.unit_abbr ?? '';

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">EBITDA by Company ({unit})</h3>
        <p className="text-xs text-neutral-500 mt-0.5">Absolute EBITDA per company over time</p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis
              width={64}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <Tooltip
              formatter={(value: number, name: string) => [formatCurrencyValue(value, region), name]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {companies.map((co, i) => (
              <Bar
                key={co.company_name}
                dataKey={co.ticker ?? co.company_name}
                fill={COMPANY_COLORS[i % COMPANY_COLORS.length]}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
